import { X, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GpsDevice } from '@/hooks/use-gps-devices';
import VehicleImage from '../gps/VehicleImage';
import DriverInfo from '../gps/DriverInfo';
import VehicleAddress from '../gps/VehicleAddress';

interface VehicleInfoWindowProps {
  device: GpsDevice;
  onClose: () => void;
  onDetailsClick: () => void;
  companyId?: string;
}

export function VehicleInfoWindow({ 
  device, 
  onClose, 
  onDetailsClick,
  companyId 
}: VehicleInfoWindowProps) {
  const formatOdometer = (value?: number) => {
    if (!value) return '0 km';
    return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;
  };

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full -mt-4 bg-background rounded-xl shadow-2xl border border-border p-4 w-[380px] z-50">
      {/* Header com imagem do veículo */}
      <div className="flex items-start gap-3 mb-3">
        <VehicleImage 
          make={device.vehicle?.make} 
          model={device.vehicle?.model} 
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base truncate">
            {device.name}
          </h3>
          <p className="text-sm text-muted-foreground">
            {device.vehicle?.make} {device.vehicle?.model}
          </p>
          <p className="text-xs text-muted-foreground font-mono">
            {device.vehicle?.plate}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Info do motorista */}
      {device.driver ? (
        <div className="mb-3 p-2.5 bg-muted/30 rounded-lg">
          <DriverInfo driver={device.driver} />
        </div>
      ) : (
        <div className="mb-3 p-2.5 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
              <span className="text-[10px] text-muted-foreground">?</span>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-muted-foreground">Sem motorista</p>
              <p className="text-[10px] text-destructive">Sem identificação</p>
            </div>
          </div>
        </div>
      )}

      {/* Métricas em grid 2 colunas */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="p-2.5 bg-muted/20 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Velocidade Atual</p>
          <p className="text-lg font-semibold">{device.speed_kmh || 0} km/h</p>
        </div>
        <div className="p-2.5 bg-muted/20 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Odômetro</p>
          <p className="text-lg font-semibold">{formatOdometer(device.odometer)}</p>
        </div>
      </div>

      {/* Local atual */}
      <div className="mb-4 p-2.5 bg-muted/20 rounded-lg">
        <p className="text-xs text-muted-foreground mb-1.5 font-medium">Local Atual</p>
        <div className="flex items-start gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-xs text-foreground leading-relaxed">
            <VehicleAddress 
              latitude={device.lat} 
              longitude={device.lon} 
              companyId={companyId} 
            />
          </div>
        </div>
      </div>

      {/* Botões de ação */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          onClick={onClose} 
          className="flex-1"
          size="sm"
        >
          Fechar
        </Button>
        <Button 
          onClick={onDetailsClick} 
          className="flex-1"
          size="sm"
        >
          Detalhes do Roteiro
        </Button>
      </div>
    </div>
  );
}
